import { Component, OnDestroy, OnInit } from '@angular/core';
import { AppConfigService } from '../../../services/config/app-config.service';
import { TokenStorageService } from '../../../services/storage/token-storage.service';
import { environment } from '../../../../environments/environment';
import {
  DomSanitizer,
  SafeResourceUrl,
  Title,
  Meta,
} from '@angular/platform-browser';
import { Router, ActivatedRoute, UrlTree } from '@angular/router';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-stoplight-apis',
    templateUrl: './rest-api.component.html',
    styleUrls: ['./rest-api.component.css'],
    standalone: false
})
export class RestApiComponent implements OnInit, OnDestroy {
  projectId: string | undefined;
  userType: string | undefined;

  iframeUrl: SafeResourceUrl | null = null;

  private messageListener!: (event: MessageEvent) => void;
  private routeSub?: Subscription;

  private suppressNextIframeSync = false;
  private lastIframePath = '';

  /**
   * event.origin is only "https://host" (no /trade), so compute origin properly.
   */
  private readonly iframeOrigin = (() => {
    try {
      const src = environment.iframeUrl || environment.iframeBaseUrl;
      return new URL(src).origin;
    } catch {
      return window.location.origin;
    }
  })();

  /**
   * If iframe is hosted under /trade, basePath will be "/trade".
   * Used to strip "/trade" from posted paths like "/trade/docs/...".
   */
  private readonly iframeBasePath = (() => {
    try {
      const src = environment.iframeBaseUrl || environment.iframeUrl;
      const p = new URL(src).pathname || '/';
      return p.replace(/\/+$/, '') || '/'; // "/trade/" -> "/trade"
    } catch {
      return '/';
    }
  })();

  constructor(
    private appConfig: AppConfigService,
    private tokenStorageService: TokenStorageService,
    private sanitizer: DomSanitizer,
    private router: Router,
    private route: ActivatedRoute,
    private title: Title,
    private meta: Meta,
  ) {
    try {
      this.userType =
        window.location.hostname === this.appConfig.priceDomain
          ? 'FEED'
          : window.location.hostname === this.appConfig.tradeDomain
            ? 'OMS'
            : '';

      if (this.userType === 'OMS') {
        if (this.tokenStorageService.getUserEnv() === 'asia') {
          this.projectId = this.appConfig.getAsiaConfig?.omsProjectId;
        } else {
          this.projectId = this.appConfig.getMenaConfig?.omsProjectId;
        }
      } else {
        this.projectId = this.appConfig.getCommonConfig?.feedProjectId;
      }
    } catch (error) {
      console.log(error);
    }
  }

  ngOnInit(): void {
    // Initial iframe URL (home). Route subscription will sync deep links.
    this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      environment.iframeUrl,
    );

    /**
     * Normalize what the iframe posts into:
     *  - appPath: "/doc/trade/..", "/updates/trade/..", "/status/trade/..", "/playground/trade/.."
     *  - fragment: "section-id"
     */
    const normalizePostedToAppPath = (raw: string) => {
      let input = raw || '';

      // If full URL, reduce to path+search+hash
      try {
        if (/^https?:\/\//i.test(input)) {
          const u = new URL(input);
          input = u.pathname + u.search + u.hash;
        }
      } catch {
        // ignore
      }

      let path = input;
      let fragment: string | undefined;

      // split hash
      const hashIdx = path.indexOf('#');
      if (hashIdx >= 0) {
        fragment = path.slice(hashIdx + 1);
        path = path.slice(0, hashIdx);
      }

      // strip query
      const qIdx = path.indexOf('?');
      if (qIdx >= 0) {
        path = path.slice(0, qIdx);
      }

      if (!path.startsWith('/')) path = '/' + path;
      path = path.replace(/\/{2,}/g, '/');

      // If iframe posts a parent-style route already, accept it
      if (
        path.startsWith('/doc/') ||
        path.startsWith('/updates/') ||
        path.startsWith('/status/') ||
        path.startsWith('/playground/')
      ) {
        return { appPath: path, fragment };
      }

      // Strip reverse-proxy base: "/trade/docs/..." -> "/docs/..."
      const base = this.iframeBasePath;
      if (
        base &&
        base !== '/' &&
        (path === base || path.startsWith(base + '/'))
      ) {
        path = path.slice(base.length);
        if (!path.startsWith('/')) path = '/' + path;
        if (path === '') path = '/';
      }

      // Home
      if (path === '/' || path === '') {
        return { appPath: '/doc/trade', fragment };
      }

      // changelog/status/playground (child-style)
      if (path.startsWith('/changelog')) {
        const remainder = path.replace(/^\/changelog\/?/, '');
        return {
          appPath: remainder ? `/updates/trade/${remainder}` : `/updates/trade`,
          fragment,
        };
      }

      if (path.startsWith('/status')) {
        const remainder = path.replace(/^\/status\/?/, '');
        return {
          appPath: remainder ? `/status/trade/${remainder}` : `/status/trade`,
          fragment,
        };
      }

      if (path.startsWith('/playground')) {
        const remainder = path.replace(/^\/playground\/?/, '');
        return {
          appPath: remainder
            ? `/playground/trade/${remainder}`
            : `/playground/trade`,
          fragment,
        };
      }

      // docs (child-style) - "/docs/<version>/..."
      if (path.startsWith('/docs')) {
        const rel = path.replace(/^\/docs\/?/, ''); // "<version>/..."
        const parts = rel.split('/').filter(Boolean);

        const version = parts[0] || '';
        if (!version) return { appPath: '/doc/trade', fragment };

        // /docs/<version>/apis/<anything...>  -> /doc/trade/<version>/apis/<anything...>
        if (parts[1] === 'apis') {
          const remainder = parts.slice(2).join('/');
          const suffix = remainder ? `/${remainder}` : '';
          return {
            appPath: `/doc/trade/${version}/apis${suffix}`,
            fragment,
          };
        }

        // /docs/<version>/<anything-else> -> /doc/trade/<version>/<anything-else>
        const remainder = parts.slice(1).join('/');
        const suffix = remainder ? `/${remainder}` : '';
        return {
          appPath: `/doc/trade/${version}${suffix}`,
          fragment,
        };
      }

      /**
       * handle Docusaurus site pages (not under /docs)
       * Example:
       *  "/developer-resources" -> "/doc/trade/developer-resources"
       */
      const relSite = path.replace(/^\/+/, ''); // "developer-resources/..."
      return {
        appPath: relSite ? `/doc/trade/${relSite}` : '/doc/trade',
        fragment,
      };
    };

    this.messageListener = (event: MessageEvent) => {
      if (event.origin !== this.iframeOrigin) return;

      const data: any = event.data;

      // Handle error signals from Docusaurus (404 / 403)
      if (data && typeof data === 'object' && data.type === 'GTN_ERROR') {
        const code = data.code === 403 ? 403 : 404;
        this.router.navigate([`/${code}`]);
        return;
      }

      // Docusaurus found no valid Keycloak session (e.g. signed out elsewhere,
      // then refreshed): clear any stale local auth state and leave the docs
      if (data && typeof data === 'object' && data.type === 'GTN_AUTH_REQUIRED') {
        this.tokenStorageService.signOut();
        this.router.navigate(['/']);
        return;
      }

      // Update title/meta if present (does NOT affect routing logic)
      if (data && typeof data === 'object') {
        const postedTitle: string | undefined = data.title;
        const postedDescription: string | undefined = data.description;

        if (postedTitle && postedTitle.trim()) {
          this.title.setTitle(postedTitle);
        }

        if (postedDescription && postedDescription.trim()) {
          this.meta.updateTag(
            { name: 'description', content: postedDescription },
            "name='description'",
          );
        }
      }

      const posted: string | undefined =
        typeof data === 'string' ? data : data?.path;

      if (!posted) return;

      const { appPath, fragment } = normalizePostedToAppPath(posted);
      if (!appPath) return;

      // Guard: ignore "unchanged" - Docusaurus sends this as fallback when version
      // resolution fails; navigating to it would overwrite the correct URL
      if (appPath.includes('/unchanged')) {
        return;
      }

      const canonical = this.canonicalizeAppPath(appPath);

      this.suppressNextIframeSync = true;
      this.router.navigateByUrl(this.toUrlTree(canonical, fragment), {
        replaceUrl: true,
      });
    };

    window.addEventListener('message', this.messageListener, false);

    // Parent route -> iframe src sync
    this.routeSub = this.route.url.subscribe(() => {
      const full = this.router.url || '';
      const tree = this.router.parseUrl(full);

      const fragment = tree.fragment || '';
      const pathOnly = full.split('?')[0].split('#')[0];
      const canonicalPathOnly = this.canonicalizeAppPath(pathOnly);

      // Fix malformed like "/doc/trade/doc/trade/..."
      if (canonicalPathOnly !== pathOnly) {
        this.router.navigateByUrl(this.toUrlTree(canonicalPathOnly, fragment), {
          replaceUrl: true,
        });
        return;
      }

      const iframePath = this.buildIframePathFromAppPath(canonicalPathOnly);
      if (!iframePath) return;

      const finalPath = fragment ? `${iframePath}#${fragment}` : iframePath;

      if (this.suppressNextIframeSync) {
        this.suppressNextIframeSync = false;
        return;
      }

      if (finalPath !== this.lastIframePath) {
        this.lastIframePath = finalPath;

        const base = (environment.iframeBaseUrl || '').replace(/\/+$/, '');
        const url = base + finalPath;

        this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
      }
    });
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.messageListener, false);
    this.routeSub?.unsubscribe();
  }

  /**
   * Fixes repeated "/doc/<section>/doc/<section>" at the start.
   * Example: "/doc/trade/doc/trade/x" -> "/doc/trade/x"
   */
  private canonicalizeAppPath(path: string): string {
    let p = (path || '').replace(/\/{2,}/g, '/');
    p = p.replace(/^\/doc\/([^/]+)(?:\/doc\/\1)+/g, '/doc/$1');
    return p;
  }

  private toUrlTree(appPath: string, fragment?: string): UrlTree {
    const segs = (appPath || '/').split('/').filter(Boolean);
    return this.router.createUrlTree(['/', ...segs], {
      fragment: fragment || undefined,
    });
  }

  /**
   * Detects a Docusaurus docs "version" segment (e.g., 1.2.1, 1.2, latest, next).
   * Used to distinguish:
   *   /doc/trade/<version>/...  (versioned docs)
   * vs
   *   /doc/trade/<page>         (site pages like developer-resources)
   */
  private isDocsVersion(seg: string): boolean {
    if (!seg) return false;
    if (seg === 'latest' || seg === 'next') return true;

    // common semver-ish patterns: 1.2, 1.2.1, 1.2.1-rc1, 1.2.1_beta, etc.
    return /^\d+\.\d+(?:\.\d+)?(?:[.-][0-9A-Za-z]+)*$/.test(seg);
  }

  /**
   * Convert Angular route into iframe path (path only, no base URL).
   * Examples:
   *  - "/doc/trade/1.2.1/..." -> "/docs/1.2.1/..."
   *  - "/doc/trade/1.2.1/apis/x" -> "/docs/1.2.1/apis/x"
   *  - "/doc/trade/developer-resources" -> "/developer-resources"
   */
  private buildIframePathFromAppPath(appPath: string): string | null {
    const canonical = this.canonicalizeAppPath(appPath);
    const trimmed = canonical.startsWith('/') ? canonical.slice(1) : canonical;
    const parts = trimmed.split('/').filter(Boolean);

    // /updates/trade/... -> /changelog/...
    if (parts[0] === 'updates' && parts[1] === 'trade') {
      const remainder = parts.slice(2).join('/');
      return remainder ? `/changelog/${remainder}` : `/changelog`;
    }

    // /status/trade/... -> /status/...
    if (parts[0] === 'status' && parts[1] === 'trade') {
      const remainder = parts.slice(2).join('/');
      return remainder ? `/status/${remainder}` : `/status`;
    }

    // /playground/trade/... -> /playground/...
    if (parts[0] === 'playground' && parts[1] === 'trade') {
      const remainder = parts.slice(2).join('/');
      return remainder ? `/playground/${remainder}` : `/playground`;
    }

    if (parts[0] !== 'doc') return null;

    const section = parts[1] || '';
    const third = parts[2] || '';

    // "/doc/trade" -> home
    if (!section || !third) return '/';

    /**
     * If the 3rd segment is NOT a version, treat it as a site page.
     * "/doc/trade/developer-resources" -> "/developer-resources"
     */
    if (!this.isDocsVersion(third)) {
      const remainder = parts.slice(2).join('/'); // "developer-resources/..."
      return remainder ? `/${remainder}` : '/';
    }

    // Versioned docs
    const version = third;

    // "/doc/trade/<version>/apis/<rest>" -> "/docs/<version>/apis/<rest>"
    if (parts[3] === 'apis') {
      const remainder = parts.slice(4).join('/');
      const suffix = remainder ? `/${remainder}` : '';
      return `/docs/${version}/apis${suffix}`;
    }

    // "/doc/trade/<version>/<rest>" -> "/docs/<version>/<rest>"
    // Bare version keeps a trailing slash: CloudFront treats a dotted last
    // segment ("1.2.1") as a file and would serve the SPA fallback instead of
    // the folder's index.html (the gtn-redirects entry page).
    const remainder = parts.slice(3).join('/');
    return remainder ? `/docs/${version}/${remainder}` : `/docs/${version}/`;
  }
}
